import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { VerifiedBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { Radio, CheckCircle2, Clock, Send } from "lucide-react";

const CATEGORIES = ["Oyun", "Sohbet (Just Chatting)", "Müzik", "Film & Dizi", "Spor", "Eğitim", "Yemek", "Teknoloji", "Diğer"];

export default function Broadcaster() {
  const { user, loading: authLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: CATEGORIES[0], social_media: "", target: "", community: "", contact: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [liveModal, setLiveModal] = useState(false);
  const [liveTitle, setLiveTitle] = useState("");
  const [liveCat, setLiveCat] = useState(CATEGORIES[0]);
  const [liveImage, setLiveImage] = useState("");
  const [liveGoal, setLiveGoal] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [starting, setStarting] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (user) {
      api.get("/broadcaster/me").then(({ data }) => { setState(data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [user, authLoading]);

  const submit = async () => {
    if (!form.category) { toast.error("Kategori seçin"); return; }
    setSubmitting(true);
    try {
      await api.post("/broadcaster/apply", form);
      toast.success("Başvurunuz alındı, inceleniyor");
      const { data } = await api.get("/broadcaster/me");
      setState(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const goLive = async () => {
    if (!liveTitle.trim()) { toast.error("Yayın başlığı girin"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/live/start", { title: liveTitle.trim(), category: liveCat, image_url: liveImage || null, goal: parseInt(liveGoal, 10) || 0 });
      navigate(`/live/${data.stream.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setStarting(false); }
  };

  const uploadLiveImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLiveImage(data.url);
      toast.success("Görsel yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
    finally { setUploadingImg(false); }
  };

  if (loading || authLoading) return <div className="min-h-screen bg-[#141414]"><Navbar /></div>;

  const isBroadcaster = state?.is_broadcaster;
  const app = state?.application;
  const pending = app && app.status === "pending";
  const rejected = app && app.status === "rejected";

  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]";

  return (
    <div className="min-h-screen bg-[#141414]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-[#E50914]/15 flex items-center justify-center"><Radio className="text-[#E50914]" size={24} /></div>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Yayıncılık</h1>
        </div>

        {isBroadcaster ? (
          <div data-testid="broadcaster-panel" className="mt-6 bg-[#181818] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 text-white text-lg font-bold">
              <CheckCircle2 className="text-[#1d9bf0]" size={22} /> Onaylı Yayıncısın <VerifiedBadge size={18} />
            </div>
            <p className="text-gray-400 mt-2 text-sm">Artık canlı yayın açabilir, ekranını paylaşabilir ve izleyicilerinden hediye alabilirsin.</p>
            <button data-testid="go-live-button" onClick={() => setLiveModal(true)} className="mt-5 inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-95">
              <Radio size={18} /> Canlı Yayın Aç
            </button>
          </div>
        ) : pending ? (
          <div data-testid="broadcaster-pending" className="mt-6 bg-[#181818] border border-white/10 rounded-xl p-8 text-center">
            <Clock className="mx-auto text-amber-400 mb-3" size={40} />
            <h3 className="text-xl font-bold text-white">Başvurun inceleniyor</h3>
            <p className="text-gray-400 mt-2 text-sm">Yayıncı başvurun yönetim ekibi tarafından değerlendiriliyor. Onaylandığında bildirim alacaksın.</p>
          </div>
        ) : (
          <div className="mt-6">
            {rejected && <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3">Önceki başvurun reddedildi. Bilgilerini güncelleyip tekrar başvurabilirsin.</div>}
            <p className="text-gray-400 text-sm mb-5">Yayıncı olmak için başvur. Onaylanan yayıncılar <span className="text-[#1d9bf0] font-medium">mavi onay tikine</span> sahip olur ve canlı yayın açabilir.</p>
            <div data-testid="broadcaster-form" className="bg-[#181818] border border-white/10 rounded-xl p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Yayın Kategorisi *</label>
                <select data-testid="bc-category" value={form.category} onChange={(e) => field("category", e.target.value)} className={input}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Sosyal Medya Adresleri</label>
                <input data-testid="bc-social" value={form.social_media} onChange={(e) => field("social_media", e.target.value)} placeholder="Instagram, YouTube, TikTok..." className={input} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Hedefin</label>
                <textarea data-testid="bc-target" value={form.target} onChange={(e) => field("target", e.target.value)} rows={2} placeholder="Yayıncılıkta hedefin nedir?" className={`${input} resize-none`} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Topluluk</label>
                <input data-testid="bc-community" value={form.community} onChange={(e) => field("community", e.target.value)} placeholder="Takipçi/topluluk büyüklüğün" className={input} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">İletişim Bilgileri</label>
                <input data-testid="bc-contact" value={form.contact} onChange={(e) => field("contact", e.target.value)} placeholder="Telefon / iletişim" className={input} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">E-posta</label>
                <input data-testid="bc-email" value={form.email} onChange={(e) => field("email", e.target.value)} placeholder={user?.email || "mail@ornek.com"} className={input} />
              </div>
              <button data-testid="bc-submit" onClick={submit} disabled={submitting} className="w-full flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-95 disabled:opacity-60">
                <Send size={16} /> {submitting ? "Gönderiliyor..." : "Başvuru Yap"}
              </button>
            </div>
          </div>
        )}

        {liveModal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="live-modal" onClick={() => !starting && setLiveModal(false)}>
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg flex items-center gap-2"><Radio className="text-[#E50914]" size={20} /> Canlı Yayın Aç</h3>
                <button onClick={() => !starting && setLiveModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              <label className="text-sm text-gray-300 mb-1.5 block">Yayın Başlığı</label>
              <input data-testid="live-title-input" value={liveTitle} onChange={(e) => setLiveTitle(e.target.value)} placeholder="Örn: Valorant oynuyoruz!" className={`${input} mb-4`} autoFocus />
              <label className="text-sm text-gray-300 mb-1.5 block">Kategori</label>
              <select data-testid="live-category-select" value={liveCat} onChange={(e) => setLiveCat(e.target.value)} className={`${input} mb-4`}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="text-sm text-gray-300 mb-1.5 block">Yayın Görseli (opsiyonel)</label>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-16 w-24 rounded-md bg-[#2B2B2B] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                  {liveImage ? <img src={mediaUrl(liveImage)} alt="" className="w-full h-full object-cover" /> : <span className="text-gray-600 text-xs">Görsel yok</span>}
                </div>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadLiveImage} data-testid="live-image-input" />
                <button type="button" onClick={() => imgRef.current?.click()} disabled={uploadingImg} className="text-sm bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md">
                  {uploadingImg ? "Yükleniyor..." : "Görsel Seç"}
                </button>
              </div>
              <label className="text-sm text-gray-300 mb-1.5 block">eCoin Hedefi (opsiyonel)</label>
              <input data-testid="live-goal-input" type="number" min="0" value={liveGoal} onChange={(e) => setLiveGoal(e.target.value)} placeholder="Örn: 5000" className={`${input} mb-5`} />
              <button data-testid="live-start-confirm" onClick={goLive} disabled={starting} className="w-full flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-95 disabled:opacity-60">
                <Radio size={18} /> {starting ? "Başlatılıyor..." : "Yayını Başlat"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
